import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { buildLabel } from '../utils/labelBuilder';
import { groupOrdersByFamily } from '../utils/orderUtils';

const { FiPlus, FiMinus, FiTrash2, FiShoppingBag } = FiIcons;

const SummaryList = ({ orders, onUpdate }) => {
  const groupedOrders = groupOrdersByFamily(orders);
  const totalItems = Object.values(orders).reduce((sum, qty) => sum + qty, 0);

  const familyColors = {
    kopi: 'from-amber-400 to-orange-500',
    teh: 'from-green-400 to-emerald-500',
    cham: 'from-purple-400 to-pink-500',
    milo: 'from-yellow-400 to-amber-500',
    barley: 'from-blue-400 to-cyan-500',
    bandung: 'from-pink-400 to-rose-500',
    'lemon-tea': 'from-lime-400 to-green-500',
    'lime-juice': 'from-lime-300 to-green-400',
    '100-plus': 'from-orange-400 to-red-500',
    coke: 'from-gray-700 to-gray-900',
    sprite: 'from-green-300 to-lime-400',
    water: 'from-blue-300 to-blue-500',
    chrysanthemum: 'from-yellow-300 to-orange-400'
  };

  return (
    <motion.section 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-6 space-y-6"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiShoppingBag} className="text-white text-sm" />
          </div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
            Order Summary
          </h2>
        </div>
        
        <div className="bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 px-4 py-2 rounded-full text-sm font-bold shadow-lg">
          {totalItems} item{totalItems !== 1 ? 's' : ''}
        </div>
      </div>

      <div className="space-y-6">
        {Object.entries(groupedOrders).map(([family, items]) => (
          <motion.div 
            key={family} 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-3"
          >
            <div className="flex items-center space-x-3">
              <div className={`w-6 h-6 bg-gradient-to-br ${familyColors[family] || 'from-gray-400 to-gray-500'} rounded-lg shadow-lg`}></div>
              <h3 className="text-lg font-bold text-gray-700 capitalize">
                {family.replace('-', ' ')}
              </h3>
            </div>
            
            <div className="space-y-3 ml-9">
              {items.map(([key, quantity]) => (
                <motion.div
                  key={key}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center justify-between bg-gradient-to-r from-gray-50/80 to-gray-100/80 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50 shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">
                      {buildLabel(key)}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <motion.button
                      whileTap={{ scale: 0.9 }}
                      onClick={() => onUpdate(key, quantity - 1)}
                      className="p-2 rounded-xl bg-gradient-to-br from-red-50 to-red-100 text-red-600 hover:from-red-100 hover:to-red-200 transition-all duration-200 min-w-[40px] min-h-[40px] flex items-center justify-center shadow-lg hover:shadow-xl"
                      aria-label={`Remove one ${buildLabel(key)}`}
                    >
                      {quantity === 1 ? (
                        <SafeIcon icon={FiTrash2} className="text-sm" />
                      ) : (
                        <SafeIcon icon={FiMinus} className="text-sm" />
                      )}
                    </motion.button>
                    
                    <div className="px-4 py-2 bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-inner border border-gray-200/50">
                      <span className="text-xl font-bold text-gray-800 min-w-[2rem] text-center block">
                        {quantity}
                      </span>
                    </div>
                    
                    <motion.button
                      whileTap={{ scale: 0.9 }}
                      onClick={() => onUpdate(key, quantity + 1)}
                      className="p-2 rounded-xl bg-gradient-to-br from-green-50 to-green-100 text-green-600 hover:from-green-100 hover:to-green-200 transition-all duration-200 min-w-[40px] min-h-[40px] flex items-center justify-center shadow-lg hover:shadow-xl"
                      aria-label={`Add one ${buildLabel(key)}`}
                    >
                      <SafeIcon icon={FiPlus} className="text-sm" />
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </motion.section>
  );
};

export default SummaryList;